import { z } from 'zod';

/**
 * Frontend-safe wire schema for GET /api/flow/accounts/:id/intel — the
 * subset of Keystone's account-state response the contact detail's
 * Intelligence panel renders. The endpoint returns the full
 * AccountStateResponse (schema in @clay-run/keystone-contract);
 * extra fields are simply not parsed here.
 *
 * The nested `state.state` is the deep, versioned Keystone payload
 * (accountFocus, signals, relationship, deal, profile). It's unstructured and
 * drifts across generator versions, so it stays `z.unknown()` here and is
 * extracted defensively in the panel (mirrors action-view's
 * TerraIntelligenceTab).
 */
export const FlowAccountIntelSchema = z.object({
  state: z.object({
    accountId: z.string(),
    icpGrade: z.string().nullable(),
    buyingIntent: z.string().nullable(),
    relationshipHealth: z.string().nullable(),
    hasChampion: z.boolean().nullable(),
    hasEconomicBuyer: z.boolean().nullable(),
    fitScoreSummary: z.string().nullish(),
    propensityScoreReasoning: z.string().nullish(),
    generatedAt: z.string().nullable(),
    /** Deep Keystone blob — wire key is `state`; extracted defensively downstream. */
    state: z.unknown().nullish(),
  }),
  accountName: z.string().nullable(),
});
export type FlowAccountIntel = z.infer<typeof FlowAccountIntelSchema>;

/**
 * `POST /api/flow/accounts/:accountId/intel-summary` request body (Key
 * Technical Decision 7, plan
 * `plans/2026-09-23-002-rep-prompt-overrides-and-account-intelligence-summary-plan.md`
 * U7).
 *
 * Deliberately EMPTY: the account id travels in the URL and the handler needs
 * nothing else (unlike the contact twin, which ships the already-on-the-client
 * contact row). It stays a POST for symmetry with the contact summary route
 * and because the frontend's `consumeTextStream`/`apiFetch` streaming path is
 * POST-shaped. Zod's default `strip` means unknown keys are ignored rather
 * than rejected, so a client that sends extra fields still gets a 200 — the
 * schema exists to reject a non-object body, not to police key sets.
 */
export const AccountIntelSummaryBodySchema = z.object({});
export type AccountIntelSummaryBody = z.infer<typeof AccountIntelSummaryBodySchema>;
